import { PendingEventType } from '@act/shared/types/feed';
import { Order } from 'react-components/member-index/sortable-table-headers';
import { PendingItemWithMember } from './expandedPendingItem.type';
import { PendingColumnKeys } from './pendingColumnKeys.type';

export function getPendingItemComparator(
  order: Order,
  orderBy: PendingColumnKeys
) {
  return order === 'desc'
    ? (a, b) => pendingItemComparator(a, b, orderBy)
    : (a, b) => -pendingItemComparator(a, b, orderBy);
}

// COMPOUND COMPARATOR
function pendingItemComparator(
  a: PendingItemWithMember,
  b: PendingItemWithMember,
  orderBy: PendingColumnKeys
) {
  const backupSorts = [
    timeDescendingComparator,
    eventTypeDescendingComparator,
    externalIdDescendingComparator,
  ];
  let firstTry;
  switch (orderBy) {
    case 'status':
      firstTry = memberStatusDescendingComparator(a, b);
      break;
    case 'event':
      firstTry = eventTypeDescendingComparator(a, b);
      break;
    case 'time':
      firstTry = timeDescendingComparator(a, b);
      break;
    case 'member':
    default:
      firstTry = memberNameDescendingComparator(a, b);
      break;
  }
  if (firstTry !== 0) {
    return firstTry;
  }
  for (const comp of backupSorts) {
    const result = comp(a, b);
    if (result !== 0) {
      return result;
    }
  }
  throw Error('comparator was not able to break tie which is unexpected');
}

// SIMPLE COMPARATORS
function timeDescendingComparator(
  a: PendingItemWithMember,
  b: PendingItemWithMember
) {
  if (a.timestamp && b.timestamp) {
    return b.timestamp - a.timestamp;
  } else if (a.timestamp) {
    return 1;
  } else if (b.timestamp) {
    return -1;
  }
  return 0;
}

function memberNameDescendingComparator(
  a: PendingItemWithMember,
  b: PendingItemWithMember
) {
  if (a.member && b.member) {
    return b.member.name.localeCompare(a.member.name);
  } else if (a.member) {
    return 1;
  } else if (b.member) {
    return -1;
  }
  return 0;
}

function eventTypeDescendingComparator(
  a: PendingItemWithMember,
  b: PendingItemWithMember
) {
  const order: PendingEventType[] = [
    'visit',
    'call',
    'message',
    'status_change_request',
    'transfer_request',
    'workflow_phase',
  ];
  return order.indexOf(b.eventType) - order.indexOf(a.eventType);
}

function memberStatusDescendingComparator(
  a: PendingItemWithMember,
  b: PendingItemWithMember
) {
  if (a.member && b.member) {
    return b.member.memberStatus.key.localeCompare(a.member.memberStatus.key);
  } else if (a.member) {
    return 1;
  } else if (b.member) {
    return -1;
  }
  return 0;
}

function externalIdDescendingComparator(
  a: PendingItemWithMember,
  b: PendingItemWithMember
) {
  return a.externalItemId - b.externalItemId;
}
